import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Search, Upload, Download, Eye, Users, Pause, Play } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config"; // Adjust path as needed
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  orderBy,

  where,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { MemberProfile } from "./MemberProfile";

interface BorrowRecord {
  id: string;
  bookId: string;
  memberId: string;
  status: "borrowed" | "returned";
}

interface UnifiedMember {
  id: string;
  name: string;
  email: string;
  memberId: string;
  role: "student" | "staff";
  phone?: string;
  joinDate: string;
  status: "active" | "suspended" | "expired";
  borrowedBooks: number;
  maxBorrowLimit: number;
  class?: string;
  grade?: string;
  department?: string;
  // Additional fields for students/staff
  middleName?: string;
  dateOfBirth?: string;
  gender?: string;
  admissionGrade?: string;
  currentClassUid?: string;
  dormitory?: string;
  guardians?: Array<{ name: string; phone: string; email: string; address: string }>;
  medicalInfo?: string;
  position?: string;
  tscNumber?: string;
  kraPin?: string;
  subjectsOfExpertiseId?: string[];
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

const MembersManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [members, setMembers] = useState<UnifiedMember[]>([]);
  const [borrowRecords, setBorrowRecords] = useState<BorrowRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState<"all" | "student" | "staff">("student");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "suspended" | "expired">("all");
  const [selectedMember, setSelectedMember] = useState<UnifiedMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);

  // Server-side Pagination State
  const [lastVisibleStudent, setLastVisibleStudent] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [lastVisibleTeacher, setLastVisibleTeacher] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [isStudentStreamExhausted, setIsStudentStreamExhausted] = useState(false);
  const [isTeacherStreamExhausted, setIsTeacherStreamExhausted] = useState(false);
  const [pageStack, setPageStack] = useState<{student: QueryDocumentSnapshot<DocumentData> | null, teacher: QueryDocumentSnapshot<DocumentData> | null}[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const studentsPerPage = 7; // More students typically
  const teachersPerPage = 3; // Fewer teachers typically
  const itemsPerPage = studentsPerPage + teachersPerPage; // 10 total



  // Computed members with actual borrowed books count
  const computedMembers = useMemo(() => {
    const activeBorrows = borrowRecords.filter(record => record.status === "borrowed");
    const borrowCountByMember = activeBorrows.reduce((acc, record) => {
      acc[record.memberId] = (acc[record.memberId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return members.map(member => ({
      ...member,
      borrowedBooks: borrowCountByMember[member.memberId] || 0,
    }));
  }, [members, borrowRecords]);

  // Auth Effect - Only handles authentication and sets schoolID
  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;

    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && (defaultAffiliation.role === "admin" || defaultAffiliation.role === "school_staff") && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

        if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
          const retryAffiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          selectedAffiliation = retryAffiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

          if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
            throw new Error(
              `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, defaultAffiliation=${JSON.stringify(
                defaultAffiliation
              )}, schoolID=${claimSchoolID}`
            );
          }
        }

        const selectedSchoolID = selectedAffiliation.schoolID || null;
        setSchoolID(selectedSchoolID);

      } catch (error: any) {
        console.error("Error verifying user claims:", error);
        setLoading(false);
        setError("Failed to verify permissions. Please contact support.");
        toast({
          title: "Missing Permissions",
          description: "You lack the necessary permissions to manage members. Please contact support.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
    });

    return () => {
      unsubscribeAuth?.();
    };
  }, [navigate, toast]);

  // Reset pagination when role filter changes
  useEffect(() => {
    // Reset to page 1 and clear stack when filter changes
    setCurrentPage(1);
    setPageStack([]);
    setLastVisibleStudent(null);
    setLastVisibleTeacher(null);
    setIsStudentStreamExhausted(false);
    setIsTeacherStreamExhausted(false);
    setMembers([]); // Clear current members to avoid mixing data
  }, [filterRole]);

  // Data Fetching Effect - Responds to schoolID, currentPage, and filterRole
  useEffect(() => {
    if (!schoolID) return;

    let unsubscribeStudents: (() => void) | undefined;
    let unsubscribeTeachers: (() => void) | undefined;
    let unsubscribeBorrowRecords: (() => void) | undefined;

    const loadMembers = () => {
      setLoading(true);

      // Dynamic Limits based on Filter Role
      let currentStudentsLimit = 0;
      let currentTeachersLimit = 0;

      if (filterRole === "student") {
        currentStudentsLimit = 10;
        currentTeachersLimit = 0;
      } else if (filterRole === "staff") {
        currentStudentsLimit = 0;
        currentTeachersLimit = 10;
      } else {
        // Default "All" mix
        currentStudentsLimit = 7;
        currentTeachersLimit = 3;
      }

      // Determine cursors for pagination
      const cursors = currentPage > 1 && pageStack.length >= currentPage - 1 
        ? pageStack[currentPage - 2] 
        : { student: null, teacher: null };

      // Fetch Students
      if (currentStudentsLimit > 0) {
        const studentsRef = collection(db, `schools/${schoolID}/students`);
        let studentsQuery = query(studentsRef, orderBy("createdAt", "desc"), limit(currentStudentsLimit));
        
        if (cursors.student) {
          studentsQuery = query(studentsRef, orderBy("createdAt", "desc"), startAfter(cursors.student), limit(currentStudentsLimit));
        }

        unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
          const studentData = snapshot.docs.map((doc) => {
             const data = doc.data();
             return {
               id: doc.id,
               name: `${data.firstName || ""} ${data.middleName || ""} ${data.lastName || ""}`.trim() || "Unknown",
               email: data.email || "",
               memberId: data.registrationNumber || "",
               role: "student" as const,
               phone: data.phone || undefined,
               joinDate: data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
               status: data.status || "active",
               borrowedBooks: 0,
               maxBorrowLimit: 3,
               class: data.className || "",
               grade: data.admissionGrade || "",
               middleName: data.middleName,
               dateOfBirth: data.dateOfBirth,
               gender: data.gender,
               admissionGrade: data.admissionGrade,
               currentClassUid: data.currentClassUid,
               dormitory: data.dormitory,
               guardians: data.guardians || [],
               medicalInfo: data.medicalInfo,
             } as UnifiedMember;
           });
           
           if (snapshot.docs.length < currentStudentsLimit) {
             setIsStudentStreamExhausted(true);
           } else {
             setIsStudentStreamExhausted(false);
           }
 
           if (snapshot.docs.length > 0) {
             setLastVisibleStudent(snapshot.docs[snapshot.docs.length - 1]);
           } else {
             setLastVisibleStudent(null);
           }
          
          setMembers(prev => {
            const teachers = prev.filter(m => m.role === "staff");
            return [...teachers, ...studentData];
          });
        });
      } else {
        // If we shouldn't fetch students, ensure they are removed from state
        setMembers(prev => prev.filter(m => m.role === "staff"));
      }

      // Fetch Teachers
      if (currentTeachersLimit > 0) {
        const teachersRef = collection(db, `schools/${schoolID}/teachers`);
        let teachersQuery = query(teachersRef, orderBy("timestamp", "desc"), limit(currentTeachersLimit));
        
        if (cursors.teacher) {
          teachersQuery = query(teachersRef, orderBy("timestamp", "desc"), startAfter(cursors.teacher), limit(currentTeachersLimit));
        }

        unsubscribeTeachers = onSnapshot(teachersQuery, (snapshot) => {
          const teacherData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: data.teacherId || doc.id,
              name: `${data.firstName || ""} ${data.middleName || ""} ${data.lastName || ""}`.trim() || "Unknown",
              email: data.email || "",
              memberId: data.employeeId || "",
              role: "staff" as const,
              phone: data.phoneNumber || undefined,
              joinDate: data.timestamp ? new Date(data.timestamp).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              status: "active",
              borrowedBooks: 0,
              maxBorrowLimit: 5,
              department: Array.isArray(data.teacherType) ? data.teacherType.join(", ") : (data.teacherType || data.position || ""),
              middleName: data.middleName,
              dateOfBirth: data.dateOfBirth,
              position: Array.isArray(data.teacherType) ? data.teacherType.join(", ") : (data.teacherType || data.position),
              tscNumber: data.tscNumber,
              kraPin: data.kraPin,
              subjectsOfExpertiseId: data.subjectsOfExpertiseId || [],
            } as UnifiedMember;
          });
 
           if (snapshot.docs.length < currentTeachersLimit) {
             setIsTeacherStreamExhausted(true);
           } else {
             setIsTeacherStreamExhausted(false);
           }
 
           if (snapshot.docs.length > 0) {
             setLastVisibleTeacher(snapshot.docs[snapshot.docs.length - 1]);
           } else {
             setLastVisibleTeacher(null);
           }

          setMembers(prev => {
            const students = prev.filter(m => m.role === "student");
            return [...students, ...teacherData];
          });
        });
      } else {
        // If we shouldn't fetch teachers, ensure they are removed from state
        setMembers(prev => prev.filter(m => m.role === "student"));
      }

      // Fetch Borrow Records (only once)
      const borrowRecordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
      const borrowRecordsQuery = query(borrowRecordsRef, where("status", "==", "borrowed"));
      unsubscribeBorrowRecords = onSnapshot(borrowRecordsQuery, (snapshot) => {
        const recordData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            bookId: data.bookId,
            memberId: data.memberId,
            status: data.status,
          } as BorrowRecord;
        });
        setBorrowRecords(recordData);
      });
      
      // Fetch total count for pagination (only on page 1)
      if (currentPage === 1) {
        const promises = [];
        if (filterRole === "student" || filterRole === "all") {
             promises.push(getCountFromServer(query(collection(db, `schools/${schoolID}/students`))));
        }
        if (filterRole === "staff" || filterRole === "all") {
             promises.push(getCountFromServer(query(collection(db, `schools/${schoolID}/teachers`))));
        }

        Promise.all(promises).then((results) => {
            const total = results.reduce((acc, curr) => acc + curr.data().count, 0);
            setTotalMembers(total);
        }).catch(err => console.error("Error fetching counts:", err));
      }

      setLoading(false);
    };

    loadMembers();

    return () => {
      unsubscribeStudents?.();
      unsubscribeTeachers?.();
      unsubscribeBorrowRecords?.();
    };
  }, [schoolID, currentPage, filterRole]);

  // Pagination handlers
  const handleNextPage = () => {
    // Check if we can proceed based on the new flags
    const canProceed = (() => {
       if (filterRole === "student") return !isStudentStreamExhausted;
       if (filterRole === "staff") return !isTeacherStreamExhausted;
       // For "All", we proceed if AT LEAST ONE stream has more data
       return !isStudentStreamExhausted || !isTeacherStreamExhausted;
    })();

    if (!canProceed) return;

    setPageStack(prev => [...prev, { 
      student: lastVisibleStudent, 
      teacher: lastVisibleTeacher 
    }]);
    setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setPageStack(prev => prev.slice(0, -1));
      setCurrentPage(prev => prev - 1);
    }
  };

  // Client-side filtering on current page
  const displayMembers = computedMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.memberId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === "all" || member.role === filterRole;
    const matchesStatus = filterStatus === "all" || member.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const estimatedTotalPages = Math.ceil(totalMembers / itemsPerPage);




  const toggleMemberStatus = async (id: string) => {
    const member = computedMembers.find(m => m.id === id);
    if (!member) return;

    const newStatus = member.status === "active" ? "suspended" : "active";
    const action = newStatus === "suspended" ? "Suspended" : "Reactivated";

    if (!schoolID) {
      toast({
        title: "System Error",
        description: "School ID not found. Please refresh the page and try again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      const basePath = member.role === "student" ? "students" : "teachers";
      const memberRef = doc(db, `schools/${schoolID}/${basePath}`, id);
      await updateDoc(memberRef, { status: newStatus });
      toast({
        title: `${action} Successful`,
        description: `${action.toLowerCase()} ${member.role} member "${member.name}".`,
        duration: 4000,
      });
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast({
        title: `${action} Failed`,
        description: `Failed to ${action.toLowerCase()} member: ${error.message}. Please try again.`,
        variant: "destructive",
        duration: 6000,
      });
    }
  };

  const handleSendReminder = (memberId: string, type: 'email' | 'sms') => {
    console.log(`Sending ${type} reminder to member ${memberId}`);
    toast({
      title: "Reminder Sent",
      description: `${type.toUpperCase()} reminder sent successfully to member ID ${memberId}.`,
      duration: 4000,
    });
  };

  const handleAddFine = (memberId: string, amount: number, reason: string) => {
    console.log(`Adding fine of $${amount} to member ${memberId} for: ${reason}`);
    toast({
      title: "Fine Added",
      description: `$${amount} fine added successfully to member ID ${memberId} for "${reason}".`,
      duration: 4000,
    });
  };

  const handleSuspendMember = (memberId: string) => {
    toggleMemberStatus(memberId);
  };

  const handleReactivateMember = (memberId: string) => {
    toggleMemberStatus(memberId);
  };

  const exportMembers = () => {
    const csvContent = [
      ["Name", "Email", "Member ID", "Role", "Phone", "Join Date", "Status", "Borrowed Books", "Max Limit", "Class/Dept"].join(","),
      ...displayMembers.map(member => [
        member.name,
        member.email,
        member.memberId,
        member.role,
        member.phone || "",
        member.joinDate,
        member.status,
        member.borrowedBooks,
        member.maxBorrowLimit,
        member.class || member.department || ""
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "library_members.csv";
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: "Members data exported to CSV successfully.",
      duration: 4000,
    });
  };

  if (loading) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <p className="text-foreground text-center">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (selectedMember) {
    return (
      <MemberProfile
        member={selectedMember}
        onBack={() => setSelectedMember(null)}
        onSendReminder={handleSendReminder}
        onAddFine={handleAddFine}
        onSuspendMember={handleSuspendMember}
        onReactivateMember={handleReactivateMember}
      />
    );
  }

  const getRoleBadge = (role: "student" | "staff") => {
    const badgeClass = role === "staff"
      ? "bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white"
      : "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${badgeClass}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const badgeClass = status === "active"
      ? "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white"
      : status === "suspended"
      ? "bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white"
      : "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${badgeClass}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
          @media (max-width: 767px) {
            /* prevent horizontal page scroll */
            body { overflow-x: hidden; }

            /* make the table container span the full viewport but keep the parent's horizontal padding
               so the first column header doesn't get clipped on small screens */
            .mobile-table-container {
              width: 100vw;
              margin-left: calc(-50vw + 50%);
              padding-left: 16px;
              padding-right: 16px;
              box-sizing: border-box;
            }

            /* ensure the table header's first cell aligns with the body cells */
            .mobile-table-container table thead th:first-child {
              padding-left: 16px;
            }

            /* keep existing per-cell padding for the book column */
            .book-cell {
              padding-left: 16px !important;
              padding-right: 16px !important;
            }
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      <div className="w-full max-w-full min-h-0 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0">
          <div className="flex flex-col gap-3 sm:gap-4 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 min-w-0 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6] h-10"
                />
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <Select value={filterRole} onValueChange={(value) => setFilterRole(value as "student" | "staff" | "all")} defaultValue="student">
                  <SelectTrigger className="w-full sm:w-32 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                    <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      All Roles
                    </SelectItem>
                    <SelectItem value="student" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      Student
                    </SelectItem>
                    <SelectItem value="staff" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      Staff
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as "active" | "suspended" | "expired" | "all")} defaultValue="all">
                  <SelectTrigger className="w-full sm:w-32 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                    <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      All Status
                    </SelectItem>
                    <SelectItem value="active" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      Active
                    </SelectItem>
                    <SelectItem value="suspended" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      Suspended
                    </SelectItem>
                    <SelectItem value="expired" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      Expired
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 justify-between">
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={exportMembers} 
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex-shrink-0"
                >
                  <Download className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="hidden sm:inline">Export CSV</span>
                  <span className="sm:hidden">Export</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex-shrink-0"
                >
                  <Upload className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="hidden sm:inline">Import CSV</span>
                  <span className="sm:hidden">Import</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <Card className="h-full flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="p-0 flex-1 min-h-0 overflow-hidden">
                <div className="h-full overflow-auto no-scrollbar">
                  <div className="mobile-table-container overflow-x-auto">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="min-w-[150px] text-black dark:text-white">Name</TableHead>
                          <TableHead className="hidden lg:table-cell w-[100px] text-black dark:text-white">ID</TableHead>
                          <TableHead className="hidden xl:table-cell min-w-[150px] text-black dark:text-white">Email</TableHead>
                          <TableHead className="w-[100px] text-black dark:text-white">Role</TableHead>
                          <TableHead className="w-[80px] text-black dark:text-white">Class</TableHead>
                          <TableHead className="w-[100px] text-black dark:text-white">Status</TableHead>
                          <TableHead className="hidden md:table-cell w-[80px] text-center text-black dark:text-white">Books</TableHead>
                          <TableHead className="w-[120px] text-right text-black dark:text-white">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayMembers.map((member) => (
                          <TableRow key={member.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                            <TableCell className="font-medium pr-2 min-w-0 text-black dark:text-white py-2">
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium">{member.name}</div>
                                <div className="text-xs text-muted-foreground lg:hidden truncate">{member.memberId}</div>
                                <div className="text-xs text-muted-foreground xl:hidden truncate">{member.email}</div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-black dark:text-white py-2">
                              <div className="truncate text-xs">{member.memberId}</div>
                            </TableCell>
                            <TableCell className="hidden xl:table-cell min-w-0 text-black dark:text-white py-2">
                              <div className="truncate text-xs">{member.email}</div>
                            </TableCell>
                            <TableCell className="py-2">{getRoleBadge(member.role)}</TableCell>
                            <TableCell className="text-xs text-black dark:text-white py-2 truncate">{member.class || "-"}</TableCell>
                            <TableCell className="py-2">{getStatusBadge(member.status)}</TableCell>
                            <TableCell className="hidden md:table-cell text-center text-xs text-black dark:text-white py-2">
                              {member.borrowedBooks}/{member.maxBorrowLimit}
                            </TableCell>
                            <TableCell className="text-right py-2">
                              <div className="flex gap-0.5 justify-end items-center">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setSelectedMember(member)} 
                                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-8 w-8 p-0"
                                >
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                </Button>

                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => toggleMemberStatus(member.id)}
                                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-8 px-2 w-auto hidden md:flex items-center gap-1"
                                >
                                  {member.status === "active" ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                  <span className="text-xs font-medium">{member.status === "active" ? "Suspend" : "Activate"}</span>
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                
                {/* Pagination Controls */}
                <div className="flex flex-col items-center gap-4 pt-4 px-4 border-t border-[#F1F5F9] dark:border-[#1E293B]">
                  <div className="text-sm text-muted-foreground text-center">
                    Page {currentPage} {totalMembers > 0 ? `of ~${estimatedTotalPages}` : ''} ({displayMembers.length} shown)
                  </div>
                  <div className="flex items-center gap-2 pb-4">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-blue-600 text-white">
                      {currentPage}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={(() => {
                        if (filterRole === "student") return isStudentStreamExhausted;
                        if (filterRole === "staff") return isTeacherStreamExhausted;
                        return isStudentStreamExhausted && isTeacherStreamExhausted;
                      })()}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>



          {displayMembers.length === 0 && (
            <Card className="flex-shrink-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No members found</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {searchTerm || filterRole !== "all" || filterStatus !== "all" ? "Try adjusting your search or filters" : "No members available"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export { MembersManagement };